import React from 'react';
import { Target, Eye, Users, BookOpen, Award, ArrowRight, Sparkles, Heart } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

export const Mission = () => {
  return (
    <section id="mission" className="relative overflow-hidden">
      {/* Hero Section with Background Image */}
      <div className="relative min-h-[600px] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={process.env.REACT_APP_CONCEPT_FACADE_URL} 
            alt="Doctrina Charter School" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navyBlue/95 via-navyBlue/85 to-navyBlue/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-tealAccent/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-tealAccent/30">
              <Sparkles className="text-tealAccent" size={20} />
              <span className="text-tealAccent font-semibold text-sm">Opening Fall 2027 • Starting Small, Growing Big</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Where <span className="text-tealAccent">Wisdom</span> Meets <span className="text-goldenOlive">Innovation</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/90 mb-8 leading-relaxed">
              A transformative middle school experience in California's Inland Empire—powered by educator-led excellence and community partnership. Starting Fall 2027 with our founding cohort.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                size="lg" 
                className="bg-tealAccent hover:bg-tealAccent/90 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Learn More
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg rounded-xl backdrop-blur-sm transition-all duration-300"
              >
                Schedule a Visit
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-tealAccent mb-1">600</div>
                <div className="text-sm text-white/80">Students</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-goldenOlive mb-1">38</div>
                <div className="text-sm text-white/80">Acre Campus</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-coralRed mb-1">5</div>
                <div className="text-sm text-white/80">Courtyards</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
      </div>

      {/* Mission & Vision Cards */}
      <div className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-4">Our Purpose</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rooted in doctrina—the pursuit of wisdom and knowledge
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Mission */}
            <Card className="border-2 border-tealAccent/20 hover:border-tealAccent transition-all duration-300 hover:shadow-2xl bg-gradient-to-br from-white to-tealAccent/5 group">
              <CardContent className="p-8">
                <div className="bg-tealAccent/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Target className="text-tealAccent" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-navyBlue mb-4">Mission</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  To create a <span className="font-semibold text-tealAccent">collaborative, teacher-led</span> learning environment that empowers students to think critically, solve problems creatively, and engage actively in their communities—through personalized education and strong partnerships with families and local organizations.
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="border-2 border-goldenOlive/20 hover:border-goldenOlive transition-all duration-300 hover:shadow-2xl bg-gradient-to-br from-white to-goldenOlive/5 group">
              <CardContent className="p-8">
                <div className="bg-goldenOlive/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Eye className="text-goldenOlive" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-navyBlue mb-4">Vision</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  A transformative middle school where <span className="font-semibold text-goldenOlive">educators shape curriculum, guide policies, and lead innovation</span>. Students thrive in a supportive, inclusive environment, and the Inland Empire community plays an active role in nurturing the next generation of leaders and changemakers.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Key Highlights */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-gradient-to-br from-tealAccent/10 to-tealAccent/5 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Users className="text-tealAccent" size={28} />
              </div>
              <h4 className="font-bold text-navyBlue text-lg mb-2">Educator-Led</h4>
              <p className="text-gray-600 text-sm">Teachers lead policy through elected Academic Senate</p>
            </div>

            <div className="bg-gradient-to-br from-goldenOlive/10 to-goldenOlive/5 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <BookOpen className="text-goldenOlive" size={28} />
              </div>
              <h4 className="font-bold text-navyBlue text-lg mb-2">Research-Based</h4>
              <p className="text-gray-600 text-sm">Proven curricula with all-green EdReports ratings</p>
            </div>

            <div className="bg-gradient-to-br from-sageGreen/10 to-sageGreen/5 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Heart className="text-sageGreen" size={28} />
              </div>
              <h4 className="font-bold text-navyBlue text-lg mb-2">Whole Child</h4>
              <p className="text-gray-600 text-sm">Academic excellence with SEL and character development</p>
            </div>

            <div className="bg-gradient-to-br from-coralRed/10 to-coralRed/5 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Award className="text-coralRed" size={28} />
              </div>
              <h4 className="font-bold text-navyBlue text-lg mb-2">Community Hub</h4>
              <p className="text-gray-600 text-sm">Serving families beyond classroom hours</p>
            </div>
          </div>

          {/* Quote Section */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-navyBlue to-navyBlue/90 rounded-2xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-tealAccent/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-goldenOlive/10 rounded-full -ml-32 -mb-32"></div>
              <div className="relative z-10">
                <div className="text-6xl text-tealAccent/30 mb-4">"</div>
                <p className="text-2xl sm:text-3xl font-serif italic text-white leading-relaxed mb-4">
                  Rooted in the spirit of Doctrina—the pursuit of wisdom and knowledge—our flexible, educator-driven model ensures we meet the evolving needs of our students while staying true to timeless teaching values.
                </p>
                <div className="w-16 h-1 bg-tealAccent mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Mission;
